/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from 'react';
import { useDepartmentContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useDepartment = () => {
    const [state, dispatch] = useDepartmentContext(); // Updated context hook name

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchDepartments = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.DEPARTMENTS, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_DEPARTMENTS, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const searchDepartments = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchDepartments();
        },
        [setFilter, setPage, fetchDepartments]
    );

    const createDepartment = useCallback(
        async (departmentData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.DEPARTMENTS, departmentData);
                dispatch({ type: ACTIONS.ADD_DEPARTMENT, payload: data });

                showSuccessMessage('Department created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateDepartment = useCallback(
        async (id, departmentData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.DEPARTMENTS}/${id}`, departmentData);
                dispatch({ type: ACTIONS.UPDATE_DEPARTMENT, payload: { id, data } });

                showSuccessMessage('Department updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteDepartment = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.DEPARTMENTS}/${id}`);
                dispatch({ type: ACTIONS.DELETE_DEPARTMENT, payload: id });

                showSuccessMessage('Department deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getDepartmentById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.DEPARTMENTS}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_DEPARTMENT, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listDepartment = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.DEPARTMENTS, page, size);
                dispatch({ type: ACTIONS.SET_DEPARTMENTS, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            // State
            departments: state.departments, // Updated to departments
            selectedDepartment: state.selectedDepartment, // Updated to selectedDepartment
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchDepartments,
            searchDepartments,
            listDepartment,

            // CRUD Operations
            createDepartment,
            updateDepartment,
            deleteDepartment,
            getDepartmentById,

            // Utilities
            clearError
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchDepartments,
            searchDepartments,
            createDepartment,
            updateDepartment,
            deleteDepartment,
            getDepartmentById,
            clearError
        ]
    );
};

export default useDepartment;
